import React, { useEffect, useState } from 'react';
import * as R from 'ramda';
import { useSelected } from 'contexts/selected';
import { useBehaviorTracker } from 'contexts/behaviors';
import { List, ListItem, AltListItem } from 'components/ProductSidebar/menus/menuComponents';
import ProductCard from 'components/ProductSidebar/ProductCard/ProductCard';
import { SelectedMenuProductList } from 'rangeConfig/@types';
import { Product } from '@inter-ikea-storageone/range';
import { sortProducts } from 'products/utils';
import useMLSortSelected from 'hooks/useMLSortSelected';
import ProductListFilters from './ProductListFilters';
import { isMobile } from 'utils/platform';
import { getProductClusters } from 'contexts/plannerConfig';
import SingleColumnProductList from 'rangeConfig/shared/widgets/frontLayouts/SingleColumnProductList';
import usePrevious from 'hooks/usePrevious';
import NoneOptionCard from 'components/ProductSidebar/ProductCard/NoneOptionCard';

type SortedProductsProps = {
  products: Product[];
  content: SelectedMenuProductList;
  onSelectItem: (product: Product, index: number) => void;
  alternativeProductList?: boolean;
};

const SortedProducts = ({
  products,
  content,
  onSelectItem,
  alternativeProductList
}: SortedProductsProps) => {
  const useMLSorting = !!content.useMLSorting && products.length > 1;

  const checkIfProductsChanged = (): boolean => {
    const sortByCoreProductId = R.sortBy(R.identity);
    const newIds = products.map(product => product.coreProductId);
    const prevIds = productsToShow.map(product => product.coreProductId);

    return useMLSorting
      ? !R.equals(newIds, prevIds)
      : !R.equals(sortByCoreProductId(newIds), sortByCoreProductId(prevIds));
  };

  const { sortedProducts: mlSortedProducts, loading: mlLoading } = useMLSortSelected(
    products,
    useMLSorting
  );

  const loading = useMLSorting ? mlLoading : false;

  const sortedProducts =
    useMLSorting && mlSortedProducts.length
      ? mlSortedProducts
      : content.sortOrder
      ? sortProducts(products, content.sortOrder)
      : sortProducts(products, [{ path: 'name' }]);

  let productClusters = content.cluster
    ? getProductClusters(sortedProducts)(content.cluster)
    : sortedProducts.map(p => [p]);

  if (content.sortClusteredProducts)
    productClusters = content.sortClusteredProducts(productClusters);

  const [productsToShow, setProductsToShow] = React.useState<Product[]>(sortedProducts);

  useEffect(() => {
    if (checkIfProductsChanged() && !loading) {
      setProductsToShow(sortedProducts);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedProducts]);

  return (
    <>
      {alternativeProductList ? (
        <AltListItem key={products[0]?.coreProductId}>
          <SingleColumnProductList
            noneOption={content.noneOption}
            onLayoutSelected={onSelectItem}
            suggestionsForSelectedFrame={sortedProducts}
            selectedLayout={content.id}
          />
        </AltListItem>
      ) : (
        <>
          {content.noneOption && (
            <ListItem key="none-option">
              {content.noneOption && (
                <NoneOptionCard noneOption={content.noneOption} currentMenu={content.id} />
              )}
            </ListItem>
          )}
          {productClusters.map((products, i) => (
            <ListItem key={products[0]?.coreProductId}>
              <ProductCard
                product={products[0]}
                cluster={products}
                handleClick={(product: Product) => onSelectItem(product, i)}
                mainImage={content.productCardMainImage}
                hoverImage={content.productCardHoverImage}
                cardTitle={content.cardsTitle}
                cardSubtitle={content.cardsSubtitle}
                showInfoBtn={!content.hideInfoButton}
                selected={content.selected}
                showPrice={!content.hidePrice}
                behaviors={content.behaviors}
                productCardIcon={content.productCardIcon}
              />
            </ListItem>
          ))}
        </>
      )}
    </>
  );
};

type Props = {
  content: SelectedMenuProductList;
  alternativeProductList?: boolean;
};

const ProductList = ({ content, alternativeProductList }: Props) => {
  const { getContentProducts } = useSelected();

  const behaviorTracker = useBehaviorTracker();
  const contentProducts = getContentProducts(content);
  const previousContentProducts = usePrevious(contentProducts);

  const [filteredProducts, setFilteredProducts] = useState(contentProducts);

  useEffect(() => {
    if (JSON.stringify(contentProducts) !== JSON.stringify(previousContentProducts)) {
      setFilteredProducts(contentProducts);
    }
  }, [contentProducts, previousContentProducts]);
  const filters = content.filters;

  const onSelectItem = (product: Product, listIndex: number) => {
    behaviorTracker.withMeta({ listIndex }).trigger(
      'productClicked',
      {
        targets: [product]
      },
      content.behaviors
    );
  };

  return (
    <>
      {filters && filters?.length > 0 && !isMobile() && (
        <ProductListFilters
          filters={filters}
          products={contentProducts}
          onProductListFilters={(products: Product[]) => {
            setFilteredProducts(products);
          }}
        />
      )}
      <List data-testid="additional-products-list" style={{ padding: 0 }}>
        <SortedProducts
          products={filteredProducts}
          content={content}
          onSelectItem={onSelectItem}
          alternativeProductList={alternativeProductList}
        />
      </List>
    </>
  );
};

export default ProductList;
